"use client";

import { useState, useCallback, useEffect } from "react";
import type { CustomInstructionsResponse, UserPreferences } from "@/app/lib/types";
import {
  clearCustomInstructionsAlert as apiClearCustomInstructionsAlert,
  deleteCustomInstructionItem as apiDeleteCustomInstructionItem,
  fetchPreferences,
  updateCustomInstructions as apiUpdateCustomInstructions,
  updatePreferences as apiUpdatePreferences,
} from "@/app/lib/api";

interface UsePreferencesReturn {
  preferences: UserPreferences | null;
  isLoading: boolean;
  updatePreferences: (prefs: UserPreferences) => Promise<void>;
  updateCustomInstructions: (text: string) => Promise<CustomInstructionsResponse>;
  deleteCustomInstructionItem: (id: string) => Promise<CustomInstructionsResponse>;
  clearCustomInstructionsAlert: () => Promise<void>;
  loadPreferences: () => Promise<void>;
}

export function usePreferences(): UsePreferencesReturn {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      const prefs = await fetchPreferences();
      setPreferences(prefs);
    } catch (err) {
      console.error("Failed to load preferences:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updatePreferencesHandler = useCallback(
    async (prefs: UserPreferences) => {
      const updated = await apiUpdatePreferences(prefs);
      setPreferences(updated);
    },
    []
  );

  const updateCustomInstructionsHandler = useCallback(async (text: string) => {
    const result = await apiUpdateCustomInstructions(text);
    setPreferences((current) => ({
      ...current,
      system_prompts: {
        ...current?.system_prompts,
        user_instructions: result.user_instructions,
      },
      custom_instruction_items: result.custom_instruction_items,
      custom_instructions_alert: result.alert,
    }));
    return result;
  }, []);

  const deleteCustomInstructionItemHandler = useCallback(async (id: string) => {
    const result = await apiDeleteCustomInstructionItem(id);
    setPreferences((current) => ({
      ...current,
      system_prompts: {
        ...current?.system_prompts,
        user_instructions: result.user_instructions,
      },
      custom_instruction_items: result.custom_instruction_items,
    }));
    return result;
  }, []);

  const clearCustomInstructionsAlertHandler = useCallback(async () => {
    await apiClearCustomInstructionsAlert();
    setPreferences((current) => current
      ? { ...current, custom_instructions_alert: null }
      : current);
  }, []);

  return {
    preferences,
    isLoading,
    updatePreferences: updatePreferencesHandler,
    updateCustomInstructions: updateCustomInstructionsHandler,
    deleteCustomInstructionItem: deleteCustomInstructionItemHandler,
    clearCustomInstructionsAlert: clearCustomInstructionsAlertHandler,
    loadPreferences,
  };
}
